import { useFormState } from 'informed';
import { useReduxState } from 'hooks';
import {
  encodeCustomAttributeKeyForForm,
  getApplicableUserCustomAttributeOptions
} from 'lib/customAttributes';
import {
  Checkbox,
  FormGroup,
  Password,
  Relevant,
  Select,
  Text,
  TextArea
} from 'components/common/form';

import type {
  User,
  UserCustomAttribute as UserCustomAttributeType
} from 'shared-types';

interface Props {
  attribute: UserCustomAttributeType;
  calledFrom:
    | 'guestInfo'
    | 'profile'
    | 'registerForm'
    | 'userCustomAttributesForm'
    | 'users';
  customAttributeOriginalValues: User['customAttributes'];
  customAttributeValues: User['customAttributes'];
  error?: any;
  id: number | string;
  isAttributeRequired: boolean;
  isReadOnly?: boolean;
  maxWidth?: string;
}

const UserCustomAttribute = ({
  attribute,
  calledFrom,
  customAttributeOriginalValues,
  customAttributeValues,
  error,
  id,
  isAttributeRequired,
  isReadOnly,
  maxWidth
}: Props) => {
  const { messages, language } = useReduxState();

  const formState = useFormState();

  const {
    description,
    descriptionTranslations,
    key,
    max,
    min,
    optionGroups,
    title,
    titleTranslations,
    type,
    validation
  } = attribute;

  const encodedKey = encodeCustomAttributeKeyForForm(key);

  const validate = (value: unknown) => {
    if (attribute.type === 'Boolean' && isAttributeRequired && !value) {
      return messages.commonFieldRequired;
    }

    if (
      attribute.type !== 'Boolean' &&
      isAttributeRequired &&
      !value?.toString().trim().length
    ) {
      return messages.commonFieldRequired;
    }

    if (attribute.type === 'String' && validation?.matchRegex && value) {
      const regex = new RegExp(validation.matchRegex);
      if (!regex.test(value as string)) {
        return (
          validation.errorMessageTranslations?.[language] ||
          validation.errorMessage ||
          messages.commonFieldInvalid
        );
      }
    }

    if (
      attribute.type === 'String' &&
      validation?.matchList?.length &&
      value &&
      !validation.matchList.includes(value as string)
    ) {
      return (
        validation.errorMessageTranslations?.[language] ||
        validation.errorMessage ||
        messages.commonFieldInvalid
      );
    }

    if (attribute.type === 'String' && validation?.validateFunction && value) {
      const validateFunction = new Function(
        'value',
        validation?.validateFunction
      );
      const isValid = validateFunction(value);
      if (!isValid) {
        return (
          validation.errorMessageTranslations?.[language] ||
          validation.errorMessage ||
          messages.commonFieldInvalid
        );
      }
    }

    return undefined;
  };

  const propsToPass: {
    field: string;
    [key: string]: any;
  } = {
    disabled: isReadOnly,
    field: `customAttributes.${encodedKey}`,
    id: `${encodedKey}-${id}`,
    label: null,
    style: {}
  };

  switch (type) {
    case 'Array':
      propsToPass.creatable = true;
      propsToPass.isMulti = true;
      propsToPass.isSearchable = true;
      propsToPass.options = []; // options cannot be undefined
      break;
    case 'ArrayWithOptions':
      propsToPass.isMulti = true;
      propsToPass.isSearchable = true;
      propsToPass.options = getApplicableUserCustomAttributeOptions({
        customAttributeValues,
        language,
        optionGroups
      });
      propsToPass.showLabelForSelectedElement = true;
      if (Array.isArray(customAttributeValues[key])) {
        propsToPass.value = (customAttributeValues[key] as string[]).map(
          (optionKey) =>
            propsToPass.options.find((option) => option.value === optionKey)
        );
      }
      break;
    case 'Boolean':
      propsToPass.label = title;
      propsToPass.style = { display: 'inline-block' };
      break;
    case 'Number':
      propsToPass.isSearchable = true;
      propsToPass.options = Array.from(Array(max - min + 1), (_, i) => ({
        label: `${i + min}`,
        value: `${i + min}`
      }));
      propsToPass.style = { maxWidth: 120 };
      break;
    case 'StringWithOptions':
      propsToPass.isSearchable = true;
      propsToPass.options = getApplicableUserCustomAttributeOptions({
        customAttributeValues,
        language,
        optionGroups
      });
      break;
    case 'Text':
      propsToPass.autoGrow = true;
      break;
    default:
      propsToPass.type = 'text';
      break;
  }

  if (
    !propsToPass.disabled &&
    (isAttributeRequired ||
      validation?.matchList?.length ||
      validation?.matchRegex ||
      validation?.validateFunction)
  ) {
    propsToPass.error = error;
    propsToPass.validate = validate;
    propsToPass.validateOnChange = isAttributeRequired || formState.submits;
  }

  if (calledFrom === 'userCustomAttributesForm') propsToPass.field = encodedKey;

  const requiredPassword = validation?.requiredPasswords?.find(
    (requiredPassword) => {
      if (!requiredPassword.valueMatchRegex) return true;

      const regex = new RegExp(requiredPassword.valueMatchRegex);
      return (
        customAttributeValues[key] &&
        regex.test(JSON.stringify(customAttributeValues[key]))
      );
    }
  );

  return (
    <>
      <FormGroup
        htmlFor={propsToPass.id}
        label={type === 'Boolean' ? '' : titleTranslations?.[language] || title}
        labelHelpText={
          type === 'Boolean'
            ? ''
            : descriptionTranslations?.[language] || description
        }
        labelIsOptional={!isAttributeRequired && !isReadOnly}
        key={encodedKey}
        maxWidth={maxWidth}
        style={
          (calledFrom === 'userCustomAttributesForm' ||
            calledFrom === 'guestInfo') &&
          isReadOnly
            ? { display: 'none' }
            : {}
        }
      >
        {type === 'Boolean' ? (
          <Checkbox
            {...propsToPass}
            {...(description
              ? {
                  labelHelpText:
                    descriptionTranslations?.[language] || description
                }
              : {})}
          />
        ) : type === 'Text' ? (
          <TextArea {...propsToPass} />
        ) : type === 'Array' ||
          type === 'ArrayWithOptions' ||
          type === 'Number' ||
          type === 'StringWithOptions' ? (
          <Select {...propsToPass} />
        ) : (
          <Text {...propsToPass} />
        )}
      </FormGroup>

      <Relevant
        when={() =>
          requiredPassword &&
          customAttributeValues[key] !== customAttributeOriginalValues[key]
        }
      >
        <FormGroup
          label={
            requiredPassword?.titleTranslations?.[language] ||
            requiredPassword?.title
          }
          style={{ marginTop: 10 }}
        >
          <Password
            field={`customAttributesRequiredPasswords.${requiredPassword?.passwordIdentifier}`}
            style={{ maxWidth: 320 }}
          />
        </FormGroup>
      </Relevant>
    </>
  );
};

export default UserCustomAttribute;
